import i18n from "i18next";
import { IconAlertTriangle, IconCircleCheck, IconCircleX, IconInfoCircle } from "@tabler/icons-react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Switch } from "@/components/lilia-ui/switch";
import { useApiContext } from "@/contexts/ApiContext";
import { testIds } from "@/testIds";
import styles from "./LaTeXExportPage.module.scss";

/** One thing a publisher would reject or query (GET …/export/publisher-check, API PublisherCompliance). */
export interface ComplianceFinding {
	severity: "error" | "warning" | "info";
	code: string;
	message: string;
	blockId?: string | null;
}

export interface ComplianceReport {
	profile: string | null;
	status: "passed" | "warnings" | "errors";
	findings: ComplianceFinding[];
	guidelinesUrl: string | null;
}

export function usePublisherCheck(documentId: string | undefined, enabled: boolean) {
	const { fetchWithAuth } = useApiContext();
	return useQuery({
		queryKey: ["publisher-check", documentId],
		enabled: enabled && !!documentId,
		// Always fresh: the author fixes something in the editor and comes back to export.
		staleTime: 0,
		queryFn: async (): Promise<ComplianceReport> => {
			const res = await fetchWithAuth(`/api/documents/${documentId}/export/publisher-check`);
			if (!res.ok) throw new Error(`publisher-check ${res.status}`);
			return res.json();
		},
	});
}

const T = testIds.latexExport;
const ICON = { error: IconCircleX, warning: IconAlertTriangle, info: IconInfoCircle } as const;

export interface SubmissionPanelProps {
	publisher: string;
	submission: boolean;
	onSubmissionChange: (on: boolean) => void;
	report: ComplianceReport | undefined;
	loading: boolean;
}

/**
 * The submission for a publisher document (publisher templates steps 6–7, 25 Oct 2026): the switch
 * for the submission package (one main.tex, .bbl, .bst, figures and a manifest), and the compliance
 * check's findings, worst first, with the publisher's guidelines.
 */
export function SubmissionPanel({ publisher, submission, onSubmissionChange, report, loading }: SubmissionPanelProps) {
	const order = { error: 0, warning: 1, info: 2 } as const;
	const findings = [...(report?.findings ?? [])].sort((a, b) => order[a.severity] - order[b.severity]);
	return (
		<section className={styles.submission} data-testid={T.submission} data-status={report?.status ?? (loading ? "loading" : "")}>
			<div className={styles.switchRow} title={i18n.t("shell:latexExport.submissionHint")}>
				<Switch
					data-testid={T.option}
					data-option="submission"
					checked={submission}
					onCheckedChange={onSubmissionChange}
				/>
				<span>{i18n.t("shell:latexExport.submission", { publisher })}</span>
			</div>
			<p className={styles.submissionStatus}>
				{loading
					? i18n.t("shell:latexExport.checking")
					: report?.status === "passed"
						? <><IconCircleCheck size={14} /> {i18n.t("shell:latexExport.checkPassed", { publisher })}</>
						: report
							? i18n.t("shell:latexExport.checkFound", {
								errors: findings.filter((f) => f.severity === "error").length,
								warnings: findings.filter((f) => f.severity === "warning").length,
							})
							: null}
			</p>
			{findings.length > 0 && (
				<ul className={styles.findings}>
					{findings.map((f, i) => {
						const Icon = ICON[f.severity];
						return (
							<li key={`${f.code}-${i}`} data-testid={T.finding} data-severity={f.severity} data-code={f.code} className={styles[f.severity]}>
								<Icon size={14} aria-label={f.severity} />
								<span>{f.message}</span>
							</li>
						);
					})}
				</ul>
			)}
			{report?.guidelinesUrl && (
				<a href={report.guidelinesUrl} target="_blank" rel="noreferrer" className={styles.guidelines} data-testid={T.guidelines}>
					{i18n.t("shell:latexExport.guidelines", { publisher })} ↗
				</a>
			)}
		</section>
	);
}

/** The IEEE PDF check's answer (GET …/export/pdf-check): the findings, and the PDF's facts. */
export interface PdfCheckReport extends ComplianceReport {
	spec: string;
	pdf: { version: string; pages: number; pageSize: string; fonts: number; images: number };
}

/** Compiles the document and reads its PDF against IEEE Xplore's rules: seconds, so on a click. */
export function usePdfCheck(documentId: string | undefined) {
	const { fetchWithAuth } = useApiContext();
	return useMutation({
		mutationFn: async (): Promise<PdfCheckReport> => {
			const res = await fetchWithAuth(`/api/documents/${documentId}/export/pdf-check`);
			const body = await res.json().catch(() => null);
			if (!res.ok) throw new Error((body as { message?: string } | null)?.message ?? `pdf-check ${res.status}`);
			return body as PdfCheckReport;
		},
	});
}

/**
 * IEEE's PDF check (publisher templates phase 3, 25 Oct 2026): IEEE Xplore's PDF specification, the
 * rules IEEE PDF eXpress applies (fonts embedded, version, security, image resolution). Lilia writes
 * IEEE documents without links or bookmarks, which the specification forbids.
 */
export function PdfCheckPanel({ documentId }: { documentId: string }) {
	const run = usePdfCheck(documentId);
	const report = run.data;
	const order = { error: 0, warning: 1, info: 2 } as const;
	const findings = [...(report?.findings ?? [])].sort((a, b) => order[a.severity] - order[b.severity]);
	return (
		<section className={styles.submission} data-testid={T.pdfCheck} data-status={report?.status ?? (run.isPending ? "loading" : "")}>
			<button type="button" className={styles.pdfCheckButton} data-testid={T.pdfCheckRun} disabled={run.isPending} onClick={() => run.mutate()}>
				{run.isPending ? i18n.t("shell:latexExport.pdfChecking") : i18n.t("shell:latexExport.pdfCheck")}
			</button>
			<p className={styles.submissionStatus}>{i18n.t("shell:latexExport.pdfCheckHint")}</p>
			{run.isError && <p className={styles.submissionStatus} data-testid={T.error}>{(run.error as Error).message}</p>}
			{report && (
				<>
					<p className={styles.submissionStatus}>
						{report.status === "passed" ? <><IconCircleCheck size={14} /> {i18n.t("shell:latexExport.pdfPassed")}</> : null}
						{" "}PDF {report.pdf.version} · {report.pdf.pages} p. · {report.pdf.pageSize}
					</p>
					<ul className={styles.findings}>
						{findings.map((f, i) => {
							const Icon = ICON[f.severity];
							return (
								<li key={`${f.code}-${i}`} data-testid={T.finding} data-severity={f.severity} data-code={f.code} className={styles[f.severity]}>
									<Icon size={14} aria-label={f.severity} />
									<span>{f.message}</span>
								</li>
							);
						})}
					</ul>
				</>
			)}
		</section>
	);
}
